import { Input } from "@comps/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@comps/ui/popover";
import { TW_Button, TW_ColorSelectorCustomColorDisplay, TW_ColorSelectorCustomColorDisplaySelected, TW_ColorSelectorCustomColorsInput, TW_ColorSelectorCustomColorsPanel, TW_ColorSelectorCustomColorsPanelTitle, TW_ColorSelectorDefaultColorsPanel, TW_ColorSelectorDefaultColorsPanelTitle, TW_ColorSelectorPanel, TW_ColorSelectorPanelItem } from "./twClasses";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ColorPicker, ColorPickerEyeDropper, ColorPickerFormat, ColorPickerHue, ColorPickerOutput, ColorPickerSelection } from "@comps/ui/shadcn-io/color-picker";
import { Button } from "@comps/ui/button";


export const DEFAULT_COMPOZZ_COLOR = "#6a06ff";
export const LINE_HOVER_COLOR = "#f9f9f9";
export const WEEKEND_COLOR = "#fbfbfb";
export const GRAY_100_COLOR = "#f7fafc";
export const GRAY_200_COLOR = "#e2e8f0";
export const GRAY_300_COLOR = "oklch(87.2% 0.01 258.338)";
export const GRAY_400_COLOR = "oklch(70.4% 0.04 256.788)";
export const GRAY_600_COLOR = "#718096";
export const GRAY_800_COLOR = "#2b3036"; //"#oklch(27.8% 0.033 256.848)";
export const LIGHT_OPACITY = 0.1;
export const HOVER_OPACITY = 0.2;

const WORKSPACE_COLORS = [
    "#a56df8",
    "#0A6D37",
    "#1D01A5",
    "#FF7A50",
    "#f4b821",
    "#721B42",
    "#79C4FB",
    "#00CC99",
    "#6B7776",
    "#FF6666",
    "#D39C62",
    "#C23B2A",
    "#6A06FF",
    "#FFBFFA",
    "#BFDFFF",
    "#B8F349",
    "#06ADB6",
    "#8EFFC7",
    "#FBC4C5",
    "#B11D85",
]

const FIELD_COLORS = [
    "#22C55E",
    "#00C78C",
    "#00A374",
    "#006666",
    "#EF4444",
    "#FF6666",
    "#E34242",
    "#CC0000",
    "#FACC15",
    "#FF9966",
    "#FF9900",
    "#FF6600",
    "#D9855A",
    "#AF5935",
    "#8B4E29",
    "#C89F52",
    "#33CCCC",
    "#338DDB",
    "#0000CC",
    "#CC99FF",
    "#6633FF",
    "#6600CC",
    "#FF99CC",
    "#CC33CC",
    "#990066",
]

const CHART_COLORS = [
    "#F26619", // orange
    "#2F4558", // dark gray-blue
    "#F6B12D", // golden orange
    "#55D5E0", // bright aqua
    "#E53935", // red
    "#335F8A", // steel blue
    "#D81B60", // crimson
    "#00897B", // dark teal
    "#EF5350", // light red
    "#43A047", // green
    "#8E24AA", // purple
    "#1E88E5", // bright blue
    "#5E35B1", // deep purple
    "#7CB342", // light green
    "#3949AB", // blue
    "#FB8C00", // vibrant orange
    "#C0CA33", // lime
    "#6D4C41", // brown
    "#FDD835", // yellow
    "#757575", // medium gray
];

const DEFAULT_COLORS = { ...WORKSPACE_COLORS };

export interface RGBColor {
    red: number;
    green: number;
    blue: number;
}

export function toRGBColor(hex: string): RGBColor {
    // strip the leading # if it's there
    let color = hex.replace(/^\s*#|\s*$/g, '');

    // convert 3 char codes --> 6, e.g. `E0F` --> `EE00FF`
    if (color.length == 3) {
        color = color.replace(/(.)/g, '$1$1');
    }
    return {
        red: parseInt(color.substring(0, 2), 16),
        green: parseInt(color.substring(2, 4), 16),
        blue: parseInt(color.substring(4, 6), 16),
    }
}

export function toHexCode(rgb: RGBColor): string {
    const red = Math.round(rgb.red).toString(16).padStart(2, '0');
    const green = Math.round(rgb.green).toString(16).padStart(2, '0');
    const blue = Math.round(rgb.blue).toString(16).padStart(2, '0');
    return `#${red}${green}${blue}`;
}

/**
 * Converts an RGB color value to HSL. Conversion formula
 * adapted from http://en.wikipedia.org/wiki/HSL_color_space.
 * Assumes r, g, and b are contained in the set [0, 255] and
 * returns h, s, and l in the set [0, 1].
 *
 * @param   Number  r       The red color value
 * @param   Number  g       The green color value
 * @param   Number  b       The blue color value
 * @return  Array           The HSL representation
 */
function rgbToHsl(red: number, green: number, blue: number) {
    const r = red / 255;
    const g = green / 255;
    const b = blue / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
        const d = max - min;
        s = (l > 0.5) ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
        }
        h = h! / 6;
    }

    return [h, s, l];
}

function hue2rgb(p: number, q: number, t: number) {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
}

/**
 * Converts an HSL color value to RGB. Conversion formula
 * adapted from http://en.wikipedia.org/wiki/HSL_color_space.
 * Assumes h, s, and l are contained in the set [0, 1] and
 * returns r, g, and b in the set [0, 255].
 *
 * @param   Number  h       The hue
 * @param   Number  s       The saturation
 * @param   Number  l       The lightness
 * @return  Array           The RGB representation
 */
function hslToRgb(h: number, s: number, l: number) {
    let r: number
    let g: number
    let b: number;

    if (s === 0) {
        r = g = b = l; // achromatic
    } else {
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }

    return [r * 255, g * 255, b * 255];
}

export function applyBrightness(rgbcode: string, percent: number) {
    const rgb = toRGBColor(rgbcode);
    const hsl = rgbToHsl(rgb.red, rgb.green, rgb.blue);
    const newBrightness = hsl[2] + hsl[2] * (percent / 100);

    const newRgb = hslToRgb(hsl[0], hsl[1], newBrightness);
    return '#'
        + convertToTwoDigitHexCodeFromDecimal(newRgb[0])
        + convertToTwoDigitHexCodeFromDecimal(newRgb[1])
        + convertToTwoDigitHexCodeFromDecimal(newRgb[2]);

}

function convertToTwoDigitHexCodeFromDecimal(decimal: number) {
    let code = Math.round(decimal).toString(16);

    if (code.length < 2) {
        code = '0' + code;
    }
    return code;
}

/**
 * Function found on the internet to determine if a color is dark or not
 * 
 * @param bgColor 
 * @returns 
 */
export function IsDarkColor(bgColor: string | null, defaultResponse: boolean = true) {
    if (!bgColor) {
        return defaultResponse;
    }
    let { red, green, blue } = toRGBColor(bgColor);
    let uicolors = [red / 255, green / 255, blue / 255];
    let c = uicolors.map((col) => {
        if (col <= 0.03928) {
            return col / 12.92;
        }
        return Math.pow((col + 0.055) / 1.055, 2.4);
    });
    let L = (0.2126 * c[0]) + (0.7152 * c[1]) + (0.0722 * c[2]);
    return L <= 0.179;
}

export function getLightColor(color: string) {
    const rgb = toRGBColor(color);
    return `rgba(${rgb.red}, ${rgb.green}, ${rgb.blue}, 0.1)`;
}

// getLightOpaqueColor returns the same tint as getLightColor but fully
// opaque (the translucent color composited over white): for sticky elements,
// where a transparent background would let the scrolling content show through.
export function getLightOpaqueColor(color: string, alpha: number = 0.1) {
    const rgb = toRGBColor(color);
    const overWhite = (channel: number) => Math.round(channel * alpha + 255 * (1 - alpha));
    return `rgb(${overWhite(rgb.red)}, ${overWhite(rgb.green)}, ${overWhite(rgb.blue)})`;
}

export function getDarkColor(color: string) {
    return applyBrightness(color, -25);
}

export function getExtraLightColor(color: string) {
    const rgb = toRGBColor(color);
    return `rgba(${rgb.red}, ${rgb.green}, ${rgb.blue}, 0.005)`;
}

export function getHoverColor(color: string) {
    const rgb = toRGBColor(color);
    return `rgba(${rgb.red}, ${rgb.green}, ${rgb.blue}, 0.2)`;
}

// export function ApplyBrightness(hex: string, percent: number) {
//     let {red, green, blue} = RGBColor(hex);

//     return '#' +
//         ((0 | (1 << 8) + red + (256 - red) * percent / 100).toString(16)).substring(1) +
//         ((0 | (1 << 8) + green + (256 - green) * percent / 100).toString(16)).substring(1) +
//         ((0 | (1 << 8) + blue + (256 - blue) * percent / 100).toString(16)).substring(1);
// }

function getColors(colorType: ColorType) {
    if (colorType === WorkspaceColorType) {
        return WORKSPACE_COLORS;
    } else if (colorType === FieldColorType) {
        return FIELD_COLORS;
    } else if (colorType === ChartColorType) {
        return CHART_COLORS;
    }
    return DEFAULT_COLORS;
}

export function getRandomColor(colorType: ColorType) {
    let colors = getColors(colorType);
    return colors[Math.floor(Math.random() * colors.length)];
}

export function getColor(colorType: ColorType, index: number) {
    let colors = getColors(colorType);
    return colors[index % colors.length];
}

export type ColorType = "Workspace" | "Field" | "Chart";
export const WorkspaceColorType: ColorType = "Workspace";
export const FieldColorType: ColorType = "Field";
export const ChartColorType: ColorType = "Chart";
export interface ColorSelectorProps {
    initialColor?: string;
    onColorSelected: (color: string) => void;
    colorType: ColorType;
}

export function ColorSelector({ initialColor, onColorSelected, colorType }: ColorSelectorProps) {

    const { t } = useTranslation();
    const [customColor, setCustomColor] = useState<string | null>(null);
    const [draftColor, setDraftColor] = useState<string | null>(null);
    const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
    const defaultColor = "#DDDDDD";
    const colors = getColors(colorType);
    const validColor = useMemo(() => getValidColor(customColor), [customColor]);
    useEffect(() => {
        if (initialColor && !colors.includes(initialColor)) {
            setCustomColor(initialColor);
        }
    }, [initialColor, colors]);

    const getOnColorClickFct = useCallback((color: string) => {
        return (event: React.MouseEvent<HTMLDivElement>) => {
            event.stopPropagation();
            event.preventDefault();
            onColorSelected(color);
        }
    }, [onColorSelected]);

    function getValidColor(color: string | null) {
        if (!color) {
            return defaultColor;
        }
        let validColor = color;
        if (!validColor.startsWith("#")) {
            validColor = "#" + validColor;
        }
        if (validColor.length < 7) {
            validColor = validColor + "000000";
        }
        if (validColor.length > 7) {
            validColor = validColor.substring(0, 7);
        }
        return validColor;
    }


    const onCustomColorConfirm = () => {
        setIsColorPickerOpen(false);
        onColorSelected(getValidColor(draftColor));
    }

    const onCustomColorInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            setIsColorPickerOpen(false);
            onColorSelected(getValidColor(customColor));
        }
    }
    const isInitialColorDefault = initialColor && colors.includes(initialColor);
    const isInitialColorCustom = initialColor && (validColor === initialColor);
    return (
        <div className={TW_ColorSelectorPanel}>
            <p className={TW_ColorSelectorDefaultColorsPanelTitle}>{t("color.selector.default.colors.title")}</p>
            <div className={TW_ColorSelectorDefaultColorsPanel}>
                {colors.map((color) => {
                    const isSelected = isInitialColorDefault && initialColor === color;
                    return (
                        <div key={color} className={TW_ColorSelectorPanelItem + (isSelected ? " border-2 border-compozz-purple" : "")} style={{ backgroundColor: color }} onClick={getOnColorClickFct(color)} />
                    )
                })}
            </div>
            <p className={TW_ColorSelectorCustomColorsPanelTitle}>{t("color.selector.custom.colors.title")}</p>
            <div className={TW_ColorSelectorCustomColorsPanel}>
                <Popover open={isColorPickerOpen} onOpenChange={setIsColorPickerOpen}>
                    <PopoverTrigger asChild>
                        <div
                            className={(isInitialColorCustom ? TW_ColorSelectorCustomColorDisplaySelected : TW_ColorSelectorCustomColorDisplay)}
                            style={{ backgroundColor: validColor }}
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setIsColorPickerOpen(true);
                            }}
                        >
                        </div>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                        <ColorPicker
                            value={validColor}
                            onChange={(color: any) => {
                                // Only call onChange if it's a real user interaction, not initial render
                                if (Array.isArray(color) && color.length >= 3) {
                                    // Convert RGB values to hex format
                                    const r = Math.round(color[0]);
                                    const g = Math.round(color[1]);
                                    const b = Math.round(color[2]);
                                    const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`.toUpperCase();
                                    setDraftColor(hex);
                                }
                            }}
                            className="max-w-sm rounded-md border bg-background p-4 shadow-sm"
                        >
                            <ColorPickerSelection />
                            <div className="flex items-center gap-4">
                                <ColorPickerEyeDropper />
                                <div className="grid w-full gap-1">
                                    <ColorPickerHue />
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <ColorPickerOutput />
                                <ColorPickerFormat showAlpha={false} />
                            </div>
                            <Button className={TW_Button + " self-end"} onClick={onCustomColorConfirm}>{t("general.button.confirm.text")}</Button>
                        </ColorPicker>

                    </PopoverContent>
                </Popover>
                <Input className={TW_ColorSelectorCustomColorsInput} placeholder={t("color.selector.input.placeholder")} onChange={(event) => setCustomColor(event.target.value)} autoFocus={false} value={customColor ?? undefined} onKeyDown={onCustomColorInputKeyDown} />
            </div>
        </div>
    )
}